import { Calendar } from 'primereact/calendar';
import { Card } from 'primereact/card';
import { Chart } from 'primereact/chart';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dropdown } from 'primereact/dropdown';
import { ProgressBar } from 'primereact/progressbar';
import { Tag } from 'primereact/tag';
import { Button } from 'primereact/button';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { makeRequest2 } from '../../../../utils/makeRequest';
import { toastify } from '../../../../utils/toast';
import { getAgentStatsURL } from '../../../../utils/urls';
import Layout from '../../../core/component/layout';

const AgentPerformance = () => {
    const [performances, setPerformances] = useState([]);
    const [loading, setLoading] = useState(false);
    const [dateRange, setDateRange] = useState([new Date(), new Date()]);
    const [selectedPeriod, setSelectedPeriod] = useState('monthly');
    const [chartData, setChartData] = useState({});
    const [chartOptions, setChartOptions] = useState({});
    const [summaryStats, setSummaryStats] = useState({
        totalTickets: 0,
        avgCsat: 0,
        avgSla: 0,
        avgResolutionTime: 0,
        totalSurveys: 0,
        surveyResponseRate: 0
    });

    const periods = [
        { label: 'Daily', value: 'daily' },
        { label: 'Weekly', value: 'weekly' },
        { label: 'Monthly', value: 'monthly' },
        { label: 'Quarterly', value: 'quarterly' },
        { label: 'Yearly', value: 'yearly' }
    ];

    useEffect(() => {
        loadPerformanceData();
    }, [selectedPeriod, dateRange]);

    const loadPerformanceData = async () => {
        setLoading(true);
        try {
            const response = await makeRequest2(getAgentStatsURL, 'GET');

            console.log('Agent stats response:', response);

            if (response.success && response.data) {
                const statsData = response.data.data || response.data;

                // Extract the data from the backend response
                const summary = statsData.summary || {};
                const departmentBreakdown = Array.isArray(statsData.departmentBreakdown) ? statsData.departmentBreakdown : [];
                const roleBreakdown = Array.isArray(statsData.roleBreakdown) ? statsData.roleBreakdown : [];
                const performance = statsData.performance || {};

                // Create performance data structure for the table - one row per department
                const performanceData = departmentBreakdown.map((dept, index) => ({
                    id: index,
                    department: dept._id || 'Unassigned',
                    total_agents: dept.count || 0,
                    active_agents: Math.round((summary.activeAgents / summary.totalAgents) * dept.count) || 0,
                    performance_scores: {
                        overall_performance_score: Math.round(performance.avgSlaCompliance || 0)
                    },
                    resolution_metrics: {
                        resolution_rate_percentage: performance.avgTicketsResolved && performance.avgTicketsHandled
                            ? Math.round((performance.avgTicketsResolved / performance.avgTicketsHandled) * 100)
                            : 0,
                        average_resolution_time_minutes: Math.round(performance.avgResolutionTime || 0)
                    },
                    sla_metrics: {
                        sla_compliance_percentage: Math.round(performance.avgSlaCompliance || 0)
                    },
                    customer_satisfaction: {
                        average_csat_score: parseFloat(performance.avgSatisfactionScore || 0).toFixed(1)
                    },
                    tickets_metrics: {
                        tickets_handled: Math.round(performance.avgTicketsHandled || 0)
                    },
                    avg_tickets_handled: Math.round(performance.avgTicketsHandled || 0),
                    avg_response_time: Math.round(performance.avgResponseTime || 0),
                    avg_satisfaction: parseFloat(performance.avgSatisfactionScore || 0).toFixed(1)
                }));

                setPerformances(performanceData.length > 0 ? performanceData : []);

                // Set summary statistics - use survey metrics if available
                const avgCsat = performance.surveyMetrics?.avgCSAT || performance.avgSatisfactionScore || 0;
                const totalSurveys = performance.surveyMetrics?.totalSurveys || 0;

                setSummaryStats({
                    totalTickets: Math.round(performance.avgTicketsHandled || 0),
                    avgCsat: parseFloat(avgCsat).toFixed(1),
                    avgSla: Math.round(performance.avgSlaCompliance || 0),
                    avgResolutionTime: Math.round(performance.avgResolutionTime || 0),
                    totalSurveys: totalSurveys,
                    surveyResponseRate: performance.surveyMetrics?.responseRate || 0
                });

                // Initialize chart with actual data
                initChart(performanceData.length > 0 ? performanceData : []);
            } else {
                toastify(response.error || 'Failed to load performance data', 'error');
                setPerformances([]);
                setSummaryStats({
                    totalTickets: 0,
                    avgCsat: 0,
                    avgSla: 0,
                    avgResolutionTime: 0,
                    totalSurveys: 0,
                    surveyResponseRate: 0
                });
                initChart([]);
            }
        } catch (error) {
            console.error('Error loading performance data:', error);
            toastify(error.message || 'Error loading performance data', 'error');
            setPerformances([]);
            setSummaryStats({
                totalTickets: 0,
                avgCsat: 0,
                avgSla: 0,
                avgResolutionTime: 0,
                totalSurveys: 0,
                surveyResponseRate: 0
            });
            initChart([]);
        }
        setLoading(false);
    };

    const initChart = (performanceData = []) => {
        if (performanceData.length === 0) {
            setChartData({});
            setChartOptions({});
            return;
        }

        const documentStyle = getComputedStyle(document.documentElement);
        const textColor = documentStyle.getPropertyValue('--text-color');
        const textColorSecondary = documentStyle.getPropertyValue('--text-color-secondary');
        const surfaceBorder = documentStyle.getPropertyValue('--surface-border');

        // Extract department labels and performance scores
        const labels = performanceData.map(dept => dept.department || 'Unassigned');
        const performanceScores = performanceData.map(dept => dept.performance_scores?.overall_performance_score || 0);
        const csatScores = performanceData.map(dept => parseFloat(dept.customer_satisfaction?.average_csat_score || 0) * 20); // Convert to percentage (out of 100)
        const slaScores = performanceData.map(dept => dept.sla_metrics?.sla_compliance_percentage || 0);

        const data = {
            labels: labels,
            datasets: [
                {
                    label: 'Overall Performance (%)',
                    data: performanceScores,
                    backgroundColor: documentStyle.getPropertyValue('--blue-500'),
                    borderColor: documentStyle.getPropertyValue('--blue-500'),
                    borderWidth: 1
                },
                {
                    label: 'CSAT Score (%)',
                    data: csatScores,
                    backgroundColor: documentStyle.getPropertyValue('--green-500'),
                    borderColor: documentStyle.getPropertyValue('--green-500'),
                    borderWidth: 1
                },
                {
                    label: 'SLA Compliance (%)',
                    data: slaScores,
                    backgroundColor: documentStyle.getPropertyValue('--orange-500'),
                    borderColor: documentStyle.getPropertyValue('--orange-500'),
                    borderWidth: 1
                }
            ]
        };

        const options = {
            maintainAspectRatio: false,
            aspectRatio: 0.8,
            plugins: {
                legend: {
                    labels: {
                        color: textColor
                    }
                },
                tooltip: {
                    callbacks: {
                        label: function(context) {
                            let label = context.dataset.label || '';
                            if (label) {
                                label += ': ';
                            }
                            if (context.parsed.y !== null) {
                                label += context.parsed.y.toFixed(1) + '%';
                            }
                            return label;
                        }
                    }
                }
            },
            scales: {
                x: {
                    ticks: {
                        color: textColorSecondary,
                        font: {
                            weight: 500
                        }
                    },
                    grid: {
                        color: surfaceBorder,
                        drawBorder: false
                    }
                },
                y: {
                    beginAtZero: true,
                    max: 100,
                    ticks: {
                        color: textColorSecondary,
                        callback: function(value) {
                            return value + '%';
                        }
                    },
                    grid: {
                        color: surfaceBorder,
                        drawBorder: false
                    }
                }
            }
        };

        setChartData(data);
        setChartOptions(options);
    };

    const departmentBodyTemplate = (rowData) => {
        return rowData.department || 'Unassigned';
    };

    const totalAgentsBodyTemplate = (rowData) => {
        return rowData.total_agents || 0;
    };

    const activeAgentsBodyTemplate = (rowData) => {
        return rowData.active_agents || 0;
    };

    const exportToCSV = () => {
        if (performances.length === 0) {
            toastify('No data to export', 'warn');
            return;
        }

        // Prepare CSV headers
        const headers = [
            'Department',
            'Total Agents',
            'Active Agents',
            'Avg Tickets Handled',
            'Resolution Rate (%)',
            'Avg Resolution Time (min)',
            'SLA Compliance (%)',
            'CSAT Score',
            'Overall Performance (%)'
        ];

        // Prepare CSV rows
        const rows = performances.map(perf => [
            perf.department || 'Unassigned',
            perf.total_agents || 0,
            perf.active_agents || 0,
            perf.tickets_metrics?.tickets_handled || 0,
            perf.resolution_metrics?.resolution_rate_percentage || 0,
            perf.resolution_metrics?.average_resolution_time_minutes || 0,
            perf.sla_metrics?.sla_compliance_percentage || 0,
            perf.customer_satisfaction?.average_csat_score || 0,
            perf.performance_scores?.overall_performance_score || 0
        ]);

        // Combine headers and rows
        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.join(','))
        ].join('\n');

        // Create blob and download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);

        link.setAttribute('href', url);
        link.setAttribute('download', `agent-performance-${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        toastify('Performance report exported successfully', 'success');
    };

    const scoreBodyTemplate = (rowData) => {
        return (
            <div className="flex align-items-center gap-2">
                <span>{rowData.performance_scores.overall_performance_score}%</span>
                <ProgressBar
                    value={rowData.performance_scores.overall_performance_score}
                    style={{ width: '100px', height: '6px' }}
                    showValue={false}
                />
            </div>
        );
    };

    const resolutionRateBodyTemplate = (rowData) => {
        return `${rowData.resolution_metrics.resolution_rate_percentage}%`;
    };

    const slaComplianceBodyTemplate = (rowData) => {
        const compliance = rowData.sla_metrics.sla_compliance_percentage;
        let severity = 'success';
        if (compliance < 70) severity = 'danger';
        else if (compliance < 85) severity = 'warning';

        return <Tag value={`${compliance}%`} severity={severity} />;
    };

    const csatScoreBodyTemplate = (rowData) => {
        return `${rowData.customer_satisfaction.average_csat_score}/5`;
    };

    const ticketsHandledBodyTemplate = (rowData) => {
        return rowData.tickets_metrics.tickets_handled;
    };

    const avgResolutionTimeBodyTemplate = (rowData) => {
        const minutes = rowData.resolution_metrics.average_resolution_time_minutes;
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return `${hours}h ${mins}m`;
    };

    return (
        <Layout>
            <div className="page-header">
                <div className="page-block">
                    <div className="row align-items-center">
                        <div className="col-md-12">
                            <ul className="breadcrumb">
                                <li className="breadcrumb-item">
                                    <Link to={"/core/dashboard"}>Dashboard</Link>
                                </li>
                                <li className="breadcrumb-item">
                                    <Link to={"/core/customer-obsession/agents"}>Agent Management</Link>
                                </li>
                                <li className="breadcrumb-item" aria-current="page">
                                    Agent Performance
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col-sm-12">
                    <div className="card">
                        <div className="card-header">
                            <div className="d-flex justify-content-between align-items-center">
                                <h5>Agent Performance Reports</h5>
                                <div className="d-flex gap-2">
                                    <Dropdown
                                        value={selectedPeriod}
                                        options={periods}
                                        onChange={(e) => setSelectedPeriod(e.value)}
                                        placeholder="Select Period"
                                    />
                                    <Calendar
                                        value={dateRange}
                                        onChange={(e) => setDateRange(e.value)}
                                        selectionMode="range"
                                        readOnlyInput
                                        hideOnRangeSelection
                                        placeholder="Select Date Range"
                                    />
                                    <Button
                                        icon="pi pi-refresh"
                                        className="p-button-outlined"
                                        onClick={loadPerformanceData}
                                        tooltip="Refresh Data"
                                    />
                                    <Button
                                        icon="pi pi-download"
                                        className="p-button-outlined"
                                        onClick={exportToCSV}
                                        tooltip="Export to CSV"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="card-body">
                            <div className="row mb-3">
                                <div className="col-12 col-md-6 col-lg-3 mb-3">
                                    <div className="card bg-light-primary">
                                        <div className="card-body">
                                            <div className="d-flex justify-content-between align-items-center">
                                                <div>
                                                    <p className="text-muted mb-2">Avg Tickets</p>
                                                    <h4 className="mb-0">{summaryStats.totalTickets}</h4>
                                                </div>
                                                <div className="avatar-sm">
                                                    <span className="avatar-title bg-primary rounded">
                                                        <i className="ti ti-ticket text-white fs-4"></i>
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-12 col-md-6 col-lg-3 mb-3">
                                    <div className="card bg-light-success">
                                        <div className="card-body">
                                            <div className="d-flex justify-content-between align-items-center">
                                                <div>
                                                    <p className="text-muted mb-2">Avg CSAT</p>
                                                    <h4 className="mb-0">{summaryStats.avgCsat}/5</h4>
                                                </div>
                                                <div className="avatar-sm">
                                                    <span className="avatar-title bg-success rounded">
                                                        <i className="ti ti-heart text-white fs-4"></i>
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-12 col-md-6 col-lg-3 mb-3">
                                    <div className="card bg-light-warning">
                                        <div className="card-body">
                                            <div className="d-flex justify-content-between align-items-center">
                                                <div>
                                                    <p className="text-muted mb-2">Avg SLA</p>
                                                    <h4 className="mb-0">{summaryStats.avgSla}%</h4>
                                                </div>
                                                <div className="avatar-sm">
                                                    <span className="avatar-title bg-warning rounded">
                                                        <i className="ti ti-clock text-white fs-4"></i>
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-12 col-md-6 col-lg-3 mb-3">
                                    <div className="card bg-light-info">
                                        <div className="card-body">
                                            <div className="d-flex justify-content-between align-items-center">
                                                <div>
                                                    <p className="text-muted mb-2">Avg Resolution</p>
                                                    <h4 className="mb-0">{Math.floor(summaryStats.avgResolutionTime / 60)}h {summaryStats.avgResolutionTime % 60}m</h4>
                                                </div>
                                                <div className="avatar-sm">
                                                    <span className="avatar-title bg-info rounded">
                                                        <i className="ti ti-chart-bar text-white fs-4"></i>
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Survey Metrics Row */}
                            <div className="row mb-3">
                                <div className="col-12 col-md-6 mb-3">
                                    <div className="card bg-light-purple">
                                        <div className="card-body">
                                            <div className="d-flex justify-content-between align-items-center">
                                                <div>
                                                    <p className="text-muted mb-2">Total Surveys</p>
                                                    <h4 className="mb-0">{summaryStats.totalSurveys}</h4>
                                                    <small className="text-muted">
                                                        {summaryStats.surveyResponseRate}% response rate
                                                    </small>
                                                </div>
                                                <div className="avatar-sm">
                                                    <span className="avatar-title bg-purple rounded">
                                                        <i className="ti ti-clipboard-check text-white fs-4"></i>
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-12 col-md-6 mb-3">
                                    <div className="card bg-light-teal">
                                        <div className="card-body">
                                            <div className="d-flex justify-content-between align-items-center">
                                                <div>
                                                    <p className="text-muted mb-2">Customer Feedback</p>
                                                    <h4 className="mb-0">{summaryStats.avgCsat}/5 CSAT</h4>
                                                    <small className="text-muted">
                                                        Based on {summaryStats.totalSurveys} surveys
                                                    </small>
                                                </div>
                                                <div className="avatar-sm">
                                                    <span className="avatar-title bg-teal rounded">
                                                        <i className="ti ti-star text-white fs-4"></i>
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="row mb-3">
                                <div className="col-12">
                                    <div className="card">
                                        <div className="card-body">
                                            <h5 className="card-title">Performance Overview</h5>
                                            {chartData && chartData.labels && chartData.labels.length > 0 ? (
                                                <Chart type="bar" data={chartData} options={chartOptions} />
                                            ) : (
                                                <div className="text-center py-5">
                                                    <i className="ti ti-chart-bar text-muted" style={{ fontSize: '3rem' }}></i>
                                                    <p className="text-muted mt-3">No performance data available</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-12">
                                    <DataTable
                                        value={performances}
                                        loading={loading}
                                        dataKey="id"
                                        paginator
                                        rows={10}
                                        rowsPerPageOptions={[5, 10, 25]}
                                        className="datatable-responsive"
                                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                                        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} department performance records"
                                        emptyMessage="No performance data found."
                                    >
                                        <Column field="department" header="Department" body={departmentBodyTemplate} sortable style={{ minWidth: '12rem' }} />
                                        <Column field="total_agents" header="Total Agents" body={totalAgentsBodyTemplate} sortable style={{ minWidth: '10rem' }} />
                                        <Column field="active_agents" header="Active Agents" body={activeAgentsBodyTemplate} sortable style={{ minWidth: '10rem' }} />
                                        <Column field="tickets_handled" header="Avg Tickets" body={ticketsHandledBodyTemplate} sortable style={{ minWidth: '10rem' }} />
                                        <Column field="resolution_rate" header="Resolution Rate" body={resolutionRateBodyTemplate} sortable style={{ minWidth: '10rem' }} />
                                        <Column field="avg_resolution_time" header="Avg Resolution Time" body={avgResolutionTimeBodyTemplate} sortable style={{ minWidth: '12rem' }} />
                                        <Column field="sla_compliance" header="SLA Compliance" body={slaComplianceBodyTemplate} sortable style={{ minWidth: '10rem' }} />
                                        <Column field="csat_score" header="CSAT Score" body={csatScoreBodyTemplate} sortable style={{ minWidth: '8rem' }} />
                                        <Column field="overall_score" header="Overall Score" body={scoreBodyTemplate} sortable style={{ minWidth: '14rem' }} />
                                    </DataTable>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default AgentPerformance;